import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { downloadDocument, fmtSize } from "@/lib/documents";
import { Panel, SectionLabel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { UploadDialog } from "@/components/documents/UploadDialog";

export const DocRow = ({ d }) => {
  const v = d.versions[d.versions.length - 1];
  return (
    <li className="flex flex-wrap items-center gap-3 py-2.5" data-testid={`doc-row-${d.id}`}>
      <FileText className="h-4 w-4 text-slate-400" />
      <div className="min-w-0 flex-1">
        <Link to={`/documenti/${d.id}`} className="block truncate text-sm font-semibold text-ink hover:text-brand-600" data-testid={`doc-link-${d.id}`}>{d.title}</Link>
        <p className="text-xs text-slate-500">{d.number} · {d.category_label}{d.ddt ? ` · DDT ${d.ddt.number}` : ""} · v{d.current_version} · {fmtSize(v.file_size)} · {fmtDate(d.updated_at)}{d.source_label ? ` · ${d.source_label}` : ""}{d.company_name ? ` · ${d.company_name}` : ""}</p>
      </div>
      {d.other_revision && <StatusBadge tone="neutral">Altra revisione</StatusBadge>}
      {d.visibility === "customer_safe" && <StatusBadge tone="green">Customer-safe</StatusBadge>}
      {d.is_archived && <StatusBadge tone="neutral">Archiviato</StatusBadge>}
      <Button size="sm" variant="ghost" onClick={() => downloadDocument(d.id, null, v.original_filename)} data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></Button>
    </li>
  );
};

// Sezione Documenti per qualsiasi scheda (cliente, offerta, ordine, contratto, intervento, rapportino, asset, licenza)
export const DocumentsPanel = ({ entityType, entityId, bare, canUpload = true }) => {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { if (can("documents:read") && entityId) api.get("/documents", { params: { entity_type: entityType, entity_id: entityId } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [entityType, entityId, can]);
  useEffect(load, [load]);
  if (!can("documents:read") || !items) return null;
  const groups = Object.entries(items.reduce((a, d) => { (a[d.category_label] = a[d.category_label] || []).push(d); return a; }, {}));
  const body = (
    <div data-testid={`documents-panel-${entityType}`}>
      <div className="flex items-center justify-between"><SectionLabel>Documenti</SectionLabel>
        {canUpload && can("documents:create") && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`documents-upload-${entityType}`}><Upload className="mr-1 h-4 w-4" />Carica</Button>}</div>
      <ul className="mt-2 divide-y divide-line">{groups.map(([label, ds]) => <li key={label} data-testid={`documents-group-${label}`}>
        {groups.length > 1 && <p className="pt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label} · {ds.length}</p>}
        <ul className="divide-y divide-line">{ds.map((d) => <DocRow key={d.id} d={d} />)}</ul></li>)}
        {!items.length && <li className="py-2 text-sm text-slate-500">Nessun documento.</li>}</ul>
      <UploadDialog open={open} onOpenChange={setOpen} entityType={entityType} entityId={entityId} onDone={load} />
    </div>);
  return bare ? body : <Panel className="p-5">{body}</Panel>;
};
